import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Info, Plus } from 'lucide-react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { Optional } from 'utility-types';
import LANGUAGES from '@/Domain/components/Settings/ai/LANGUAGES';
import LanguageControl from '@/Domain/components/Settings/ai/LanguageControl';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
} from '@/Domain/components/Shared/Field/Field';
import { useSubscription } from '@/Domain/components/SubscriptionProvider';
import { useAutoSubmit } from '@/Domain/hooks/App/useAutoSave.hook';
import { cn } from '@/Domain/utils/cn';
import { AISettings } from '@/UI/Identity/ConversationStyle/ConversationStyle.page';

type Props = {
  data: AISettings;
};

const formId = 'settings-ai';

export const LanguageSettingPage = ({ data }: Props) => {
  const { t, i18n } = useTranslation(['settings', 'common']);
  const queryClient = useQueryClient();

  const { getConfig, isFetched } = useSubscription();

  const form = useForm<AISettings>({ defaultValues: data as AISettings });
  const { control } = form;

  const onClickAddLanguage = () => {
    const languages = form.getValues('languages');
    const uiLanguage = i18n.language.split('-')[0] as (typeof LANGUAGES)[number];
    const candidates: (typeof LANGUAGES)[number][] = [
      uiLanguage,
      'en' as (typeof LANGUAGES)[number],
      ...LANGUAGES,
    ];
    const next = candidates.find((lang) => LANGUAGES.includes(lang) && !languages.includes(lang));
    if (!next) return;
    form.setValue('languages', [...languages, next], { shouldDirty: true });
  };

  const mutation = useMutation<Partial<AISettings>, unknown, Optional<AISettings>>({
    mutationKey: ['settings'],
    onMutate: (updatedData) => {
      queryClient.setQueryData(['settings'], () => ({ ...data, ...updatedData }));
    },
    onError: (_, prevData) => {
      queryClient.setQueryData(['settings'], () => prevData);
    },
  });

  useAutoSubmit<AISettings>(form, (data) => mutation.mutate(data));

  const languageLimit = getConfig('languages') as number;
  const freeMessagesLimit = getConfig('free_messages') as number;

  return (
    <FormProvider {...form}>
      <form id={formId} />

      <div className="mb-6 flex items-start gap-2 rounded-lg bg-info-muted p-3 text-sm text-info">
        <Info className="mt-0.5 size-4 shrink-0" />
        <span>{t('languages.legacy_notice')}</span>
      </div>

      <Field>
        <FieldLabel>{t('languages.title')}</FieldLabel>
        <FieldDescription>{t('languages.description')}</FieldDescription>

        <Controller
          name="languages"
          control={control}
          render={({ field, fieldState: { error } }) => {
            const count = field.value.length;
            const atLimit = isFetched && count >= languageLimit;
            const hasItems = count > 0;

            return (
              <>
                <div
                  className={cn(
                    'mt-3 rounded-xl border bg-card transition-colors',
                    error ? 'border-destructive' : 'border-border',
                  )}
                  data-testid="multilinguality-section"
                >
                  {hasItems && (
                    <div className="divide-y divide-border">
                      {field.value.map((value, index) => (
                        <LanguageControl
                          key={index}
                          index={index}
                          name={`${field.name}.${index}`}
                          onChange={(idx, v) => {
                            field.onChange(field.value.map((lng, i) => (i === idx ? v : lng)));
                          }}
                          onDelete={(idx) => {
                            field.onChange(field.value.filter((_, i) => i !== idx));
                          }}
                          value={value}
                        />
                      ))}
                    </div>
                  )}

                  <div
                    className={cn(
                      'flex items-center justify-between bg-accent px-3 py-2',
                      hasItems ? 'rounded-b-xl border-t border-border' : 'rounded-xl',
                    )}
                  >
                    <Button
                      type="button"
                      onClick={onClickAddLanguage}
                      disabled={atLimit}
                      variant="secondary"
                      size="xs"
                      data-testid="add-language-button"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      {t('languages.add')}
                    </Button>
                    <span className="text-xs text-foreground-subtle">
                      {isFetched ? `${count} / ${languageLimit}` : count}
                    </span>
                  </div>
                </div>

                <FieldError>{error?.message}</FieldError>

                {atLimit && (
                  <p className="mt-3 text-sm text-foreground-subtle">
                    <Trans
                      i18nKey="common:upgrade_package"
                      t={t}
                      values={{ limit: freeMessagesLimit }}
                      components={{
                        upgrade: (
                          <Link
                            to="../subscription"
                            className="text-sm text-foreground font-medium"
                          />
                        ),
                      }}
                    />{' '}
                    {t('languages.upgrade')}
                  </p>
                )}
              </>
            );
          }}
        />
      </Field>
    </FormProvider>
  );
};
